import React, { useState } from 'react';
import cx from 'classnames';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import FadeIn from 'react-fade-in';
import Link from 'components/consumer/Link';
import { gql } from '@apollo/client';

import useSuspenseQuery from 'commons/useSuspenseQuery';
import IconShipment from 'components/icons/iconShipment';
import IconMapMarker from 'components/icons/IconMapMarker2';
import CtaButton from 'components/essentials/Cta/CtaButton';
import IconCircleTick from 'components/icons/iconCircleTick';
import { colorBrand, colorWhite } from 'styles/layout/colors';
import { getProductURL } from 'components/consumer/cart/utils';
import IconInHomeDelivery from 'components/icons/iconInHomeDelivery';
import IconArrowDownSimple from 'components/icons/iconArrowDownSimple';
import IconArrowRightSimple from 'components/icons/iconArrowRightSimple';
import withLoginPage from 'components/consumer/consumer-login/login-page/hoc/withLoginPage';
import ProductImage from 'components/consumer/cart/components/ProductImage';
import useScreen from 'hooks/useScreen';
import useUserData from 'data-hooks/useUserData';
import SanitizedHtml from 'components/consumer/SanitizedHtml';

const HORIZONTAL_STATUSES = [
  {
    status: 1,
    title: 'Ordered',
    titlePast: 'Ordered',
  },
  {
    status: 2,
    title: 'Building',
    titlePast: 'Built',
  },
  {
    status: 3,
    title: 'Shipping',
    titlePast: 'Shipped',
  },
  {
    status: 4,
    title: 'Arriving at hub',
    titlePast: 'Arrived at hub',
  },
  {
    status: 5,
    title: 'Delivery',
    titlePast: 'Delivered',
  },
];

const VERTICAL_STATUES = [
  {
    status: 1,
    title: 'Ordered on',
  },
  {
    status: 2,
    title: 'Production scheduled',
    desc: `Our factory team has begun preparations to start building your piece.`,
  },
  {
    status: 3,
    title: 'Bringing your piece(s) to life',
    desc:
      'This is where the magic happens. Our talented craftsmen are busy assembling all our customizable upholstered pieces by hand, bringing you a quality piece you’ll love for years to come.',
  },
  {
    status: 4,
    title: 'Packing it up',
    desc:
      'We’re inspecting your piece to make sure it meets our quality standards, and prepping it to ship from our factories to our delivery partners.',
  },
  {
    status: 5,
    title: 'On its way to our delivery partner',
    desc: `Your order has officially left our factory and is headed to one of our delivery partners who is local to your area. They’re the ones who will help coordinate your delivery.`,
  },
  {
    status: 6,
    title: 'Arrival at your local delivery hub',
    desc:
      'We’ve made it to the home stretch! Your order has arrived at your local delivery hub. Our delivery partners will reach out to you shortly to coordinate a delivery window that works best for you and your schedule.',
  },
  {
    status: 7,
    title: 'Estimated Delivery',
    curbDesc:
      'Curbside: Curbside delivery is also known as Threshold delivery. The delivery truck will pull up to your home’s closest entrance and unload the package either onto the sidewalk or to your door. You are then responsible for unpacking (some items may be in a crate), bringing the item inside, and disposing of all packaging.',
    inHomeDesc:
      'In Home: Your furniture will arrive by a two-person team that will deliver your item to the room of your choice, including up to two flights of stairs. They will handle unpacking, installation, and packaging removal—all you have to do is open the door for them. We strongly recommend removing your existing furniture prior to delivery.',
  },
];

const GET_ORDER_TRACKING_DETAILS = gql`
  query getOrderTrackingDetails($order_id: String!) {
    getOrderTrackingDetails(order_id: $order_id) {
      vertical
      horizontal
      delivery_text
      display_delivery_date
      mainDesc
      progress
      order_date
      errors {
        code
        message
      }
    }
  }
`;

// transform variantsText xml format to string
const transformVariantsText = variantsText =>
  variantsText
    .replace(/<dd>|<dt>/g, '')
    .split('</dd>')
    .filter(str => str?.includes('Fabric'))
    .join(' ')
    .split('</dt>')[1];

const OrderTrackingPage = ({ orderId }) => {
  const { isMobile } = useScreen();
  const [toggle, setToggle] = useState({});
  const { isLoggedIn } = useUserData();

  const { loading, data } = useSuspenseQuery(GET_ORDER_TRACKING_DETAILS, {
    skip: !isLoggedIn || !orderId,
    fetchPolicy: 'network-only',
    variables: {
      order_id: orderId?.toString(),
    },
  });

  if (loading || !isLoggedIn) {
    return <div className="p-8 mx-auto max-w-max">loading...</div>;
  }

  const {
    vertical,
    horizontal,
    delivery_text,
    display_delivery_date,
    mainDesc,
    progress,
    order_date,
    errors,
  } = data?.getOrderTrackingDetails || {};
  const verticalStatus = Math.max(
    ...(vertical?.map(v => parseInt(v.status, 10)) || [])
  );

  if (errors) {
    return (
      <div className="text-lg font-bold text-center pt-16 h-[calc(100vh-340px)]">
        {get(errors, '0.message', 'Unable to loading tracking details')}
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 mx-auto max-w-max">
      <div className="flex flex-row items-center">
        <IconShipment />
        <div className="px-2 text-brand text-base font-sans font-bold">
          Track Order #: J-{orderId}
        </div>
      </div>
      <div className="flex flex-col md:flex-row justify-between items-center pb-6 md:pb-0">
        <div className="flex-1">
          {delivery_text && (
            <div className="mt-4 text-gray text-3xl">
              <div className="font-bold font-sans">{delivery_text}</div>
              <div className="font-bold font-sans">{display_delivery_date}</div>
            </div>
          )}

          {mainDesc && (
            <SanitizedHtml className="mt-4 text-lg font-bold" html={mainDesc} />
          )}
        </div>
        <div className="mt-4 md:mt-0 flex flex-row flex-wrap justify-between flex-1 min-w-[100vw] md:min-w-[unset]">
          {HORIZONTAL_STATUSES.map((item, idx) => {
            const status = horizontal?.status;
            const isComplete = item.status === 5 && progress === 100;
            return (
              <div
                key={item.status}
                className={cx(
                  'relative w-1/5 flex flex-col items-center justify-center',
                  idx > 0
                    ? `before:absolute before:content[''] before:block before:h-[3px] before:top-[22px] before:left-[calc(-50%_+_12px)]`
                    : null,
                  idx > 0 && item.status === status
                    ? 'before:bg-[repeating-linear-gradient(to_right,_#107c8c_0,#107c8c_8px,transparent_8px,transparent_11px)] before:w-[calc(100%_-_32px)]'
                    : null,
                  idx > 0 && item.status < status
                    ? 'before:bg-[repeating-linear-gradient(to_right,_#107c8c_0,#107c8c_8px,transparent_8px,transparent_11px)] before:w-[calc(100%_-_24px)]'
                    : null,
                  idx > 0 && item.status > status
                    ? 'before:bg-[repeating-linear-gradient(to_right,_#ccc_0,#ccc_8px,transparent_8px,transparent_11px)] before:w-[calc(100%_-_24px)]'
                    : null
                )}
              >
                <div
                  className={cx(
                    'relative rounded-full flex items-center justify-center',
                    item.status === status
                      ? 'h-[44px] w-[44px] border border-brand z-[1]'
                      : 'h-[24px] w-[24px]',
                    isComplete || item.status < status
                      ? 'bg-brand'
                      : item.status === status
                      ? 'bg-white'
                      : null
                  )}
                >
                  {isComplete || item.status < status ? (
                    <IconCircleTick
                      color={colorWhite}
                      width={isComplete ? 20 : 14}
                      height={isComplete ? 20 : 14}
                    />
                  ) : item.status === status ? (
                    <IconMapMarker width={22} color={colorBrand} />
                  ) : (
                    <div className="relative rounded-full h-[24px] w-[24px] border-2 border-gray-light4" />
                  )}
                </div>
                <p className="absolute top-[50px] text-xs md:text-sm font-bold font-sans text-center">
                  {item.status < status ? item.titlePast : item.title}
                </p>
              </div>
            );
          })}
        </div>
      </div>
      <div className="w-full h-px my-8 border border-gray-light6" />

      <div className="mx-auto my-0 mt-4">
        <div className="grid grid-cols-12 text-gray-50">
          {vertical?.length > 0 &&
            VERTICAL_STATUES.map(item => {
              const cart = vertical.filter(
                v => parseInt(v.status, 10) === item.status
              );

              const colDesc = (
                <div
                  className={cx(
                    !isMobile && 'col-start-1 col-end-3',
                    'mr-10 md:mx-auto relative w-[200px]',
                    item.status === 7
                      ? 'flex flex-col justify-end items-start mt-4'
                      : item.status !== 1
                      ? 'mt-8'
                      : null
                  )}
                >
                  {item.status === 1 ? (
                    order_date && (
                      <div className="text-xl font-bold font-sans mb-2 md:mb-0">
                        Ordered on {order_date}
                      </div>
                    )
                  ) : (
                    <>
                      <div className="text-sm mb-1">{item.title}</div>
                      <CtaButton
                        size="sm"
                        spacing="xs"
                        color="brand"
                        styling="link"
                        noLinkPadding
                        hasArrow={false}
                        style={{ text: { display: 'flex' } }}
                        onClick={() =>
                          setToggle({
                            ...toggle,
                            [item.status]: !toggle[item.status],
                          })
                        }
                      >
                        What&apos;s this ? &nbsp;
                        {toggle[item.status] ? (
                          <IconArrowDownSimple color={colorBrand} />
                        ) : (
                          <IconArrowRightSimple
                            color={colorBrand}
                            width={9}
                            height={9}
                          />
                        )}
                      </CtaButton>
                      {toggle[item.status] && (
                        <FadeIn>
                          <div className="text-sm font-sans font-normal mb-2">
                            {item.status === 7
                              ? vertical?.[0].order_cart?.delivery_method ===
                                'inhome_delivery'
                                ? item.inHomeDesc
                                : item.curbDesc
                              : item.desc}
                          </div>
                        </FadeIn>
                      )}
                    </>
                  )}
                </div>
              );

              return (
                <div key={item.title} className="contents">
                  {!isMobile && colDesc}
                  <div
                    className={cx(
                      'md:mr-10 md:mx-auto relative',
                      isMobile
                        ? 'col-start-1 col-end-2'
                        : 'col-start-3 col-end-4'
                    )}
                  >
                    <div className="h-full w-6 flex flex-col items-center justify-center">
                      {item.status === 1 ? (
                        <div
                          className={cx(
                            'h-full w-1',
                            item.status < verticalStatus
                              ? 'bg-brand'
                              : 'bg-gray-light7'
                          )}
                        />
                      ) : item.status === 7 ? (
                        <div
                          className={cx(
                            'h-full w-1',
                            item.status === verticalStatus
                              ? 'bg-brand'
                              : 'bg-gray-light7'
                          )}
                        />
                      ) : (
                        <>
                          <div
                            className={cx(
                              'h-[32px] w-1',
                              item.status <= verticalStatus
                                ? 'bg-brand'
                                : 'bg-gray-light7'
                            )}
                          />
                          <div
                            className={cx(
                              'h-[calc(100%_-_32px)] w-1',
                              item.status < verticalStatus
                                ? 'bg-brand'
                                : 'bg-gray-light7'
                            )}
                          />
                        </>
                      )}
                    </div>
                    <div
                      className={cx(
                        'w-6 h-6 absolute rounded-full text-center',
                        item.status === 1
                          ? 'top-0'
                          : item.status === 7
                          ? 'bottom-0'
                          : 'top-[32px]',
                        (item.status === 7 && progress === 100) ||
                          item.status < verticalStatus
                          ? 'bg-brand border-4 border-white'
                          : item.status === verticalStatus
                          ? 'bg-white border-4 border-brand'
                          : item.status > verticalStatus
                          ? 'bg-gray-light7 border-4 border-white'
                          : null
                      )}
                    />
                  </div>
                  <div
                    className={cx(
                      isMobile
                        ? 'col-start-2 col-end-12'
                        : 'col-start-4 col-end-12',
                      'px-4 mr-auto w-full',
                      item.status === 7
                        ? 'pt-4'
                        : item.status === 1
                        ? 'mb-4'
                        : null
                    )}
                  >
                    {isMobile && colDesc}
                    {item.status === 1 && order_date && (
                      <div className="mb-2">
                        <span className="bg-brand-lightest px-4 py-1 text-brand-darker text-sm font-sans w-auto">
                          Completed {order_date}
                        </span>
                      </div>
                    )}
                    {item.status === 7 &&
                      progress === 100 &&
                      display_delivery_date && (
                        <div className="mb-2 mt-4 md:mt-0">
                          <span className="bg-brand-light px-4 py-2 text-sm mb-6">
                            Completed {display_delivery_date}
                          </span>
                          <div className="flex flex-row text-center mt-4">
                            <IconInHomeDelivery width={24} height={24} />
                            &nbsp;
                            <span className="text-sm">In-Home Delivery</span>
                          </div>
                        </div>
                      )}
                    <div className="flex flex-col md:flex-row md:flex-wrap">
                      {cart?.length > 0 &&
                        cart.map(c => {
                          const productLinkWithOptions = getProductURL({
                            productUrlKey: c.product.url_key,
                            options: c.product.options,
                            pickLater: c.product.pickLater,
                          });
                          return (
                            <div
                              key={c.fk_cart_id}
                              className="bg-white shadow-md rounded-xl flex flex-row items-center justify-between p-4 mr-4 mt-4 md:max-w-[48%]"
                            >
                              <Link to={productLinkWithOptions}>
                                <ProductImage
                                  product={c.product}
                                  width={100}
                                  trim="color"
                                  trimPad={20}
                                />
                              </Link>
                              <div className="flex flex-col pl-2">
                                <div className="text-base font-bold font-sans mb-1">
                                  {c.product?.name}
                                </div>
                                {c.order_cart?.variants_text && (
                                  <div className="text-sm font-sans text-light2 mb-0.5">
                                    {transformVariantsText(
                                      c.order_cart.variants_text
                                    )}
                                    <span className="inline-block w-1.5 h-1.5 bg-gray rounded-full mx-2 relative top-[-2px]" />
                                    Qty {c.order_cart.quantity}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};

OrderTrackingPage.propTypes = {
  orderId: PropTypes.string,
};

export default withLoginPage(OrderTrackingPage);
